import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

/* ─── Geometric SVG background — pure CSS, no external deps ─── */
function LoginBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Deep navy base */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(150deg, #060F1E 0%, #0A192F 45%, #0D2140 100%)' }} />

      {/* Large diagonal gold line — architectural feel */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
        {/* Grid lines — very subtle */}
        {[...Array(8)].map((_, i) => (
          <line
            key={`h${i}`}
            x1="0" y1={`${12.5 * (i + 1)}%`}
            x2="100%" y2={`${12.5 * (i + 1)}%`}
            stroke="rgba(183,149,91,0.04)" strokeWidth="1"
          />
        ))}
        {[...Array(8)].map((_, i) => (
          <line
            key={`v${i}`}
            x1={`${12.5 * (i + 1)}%`} y1="0"
            x2={`${12.5 * (i + 1)}%`} y2="100%"
            stroke="rgba(183,149,91,0.04)" strokeWidth="1"
          />
        ))}

        {/* Large diagonal accent line */}
        <line x1="-10%" y1="110%" x2="110%" y2="-10%" stroke="rgba(183,149,91,0.12)" strokeWidth="1.5" />
        <line x1="-10%" y1="95%" x2="95%" y2="-10%" stroke="rgba(183,149,91,0.06)" strokeWidth="1" />

        {/* Corner geometry — bottom-left */}
        <rect x="0" y="75%" width="30%" height="1" fill="rgba(183,149,91,0.2)" />
        <rect x="0" y="0" width="1" height="35%" fill="rgba(183,149,91,0.15)" />

        {/* Gold accent square — top right */}
        <rect x="78%" y="8%" width="14%" height="14%" rx="0"
          fill="none" stroke="rgba(183,149,91,0.10)" strokeWidth="1" />
        <rect x="80%" y="10%" width="10%" height="10%" rx="0"
          fill="none" stroke="rgba(183,149,91,0.06)" strokeWidth="1" />

        {/* Dot matrix — lower right */}
        {[...Array(5)].map((_, row) =>
          [...Array(5)].map((_, col) => (
            <circle
              key={`d${row}${col}`}
              cx={`${78 + col * 4}%`} cy={`${68 + row * 4}%`}
              r="1.5" fill="rgba(183,149,91,0.18)"
            />
          ))
        )}

        {/* Large faint circle — upper left */}
        <circle cx="15%" cy="30%" r="18%" fill="none" stroke="rgba(183,149,91,0.05)" strokeWidth="1" />
        <circle cx="15%" cy="30%" r="12%" fill="none" stroke="rgba(183,149,91,0.04)" strokeWidth="1" />
      </svg>

      {/* Subtle vignette */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 60% 50%, transparent 30%, rgba(4,10,20,0.55) 100%)' }} />
    </div>
  );
}

/* ─── BA Monogram Logo ─── */
function BALogo({ size = 40 }) {
  return (
    <img src="/brand/logo-mark.png" alt="Best Agent"
      style={{ width: size, height: size, flexShrink: 0 }}
      className="rounded-[5px] object-cover" data-testid="login-logo-img" />
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) nav("/", { replace: true });
    else setErr(r.error || "Credenziali non valide");
  };

  return (
    <div
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[1.15fr_1fr]"
      style={{ background: '#F0F2F5' }}
      data-testid="login-page"
    >
      {/* ── Left panel ── */}
      <div className="relative hidden lg:flex flex-col justify-between p-14 overflow-hidden">
        <LoginBackground />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3.5 animate-fade-in">
          <BALogo size={44} />
          <div>
            <div className="text-white font-serif text-xl font-light tracking-tight leading-none">Best Agent</div>
            <div className="text-[10px] uppercase tracking-[0.28em] mt-0.5" style={{ color: 'var(--gold)' }}>Private Wealth CRM</div>
          </div>
        </div>

        {/* Hero text */}
        <div className="relative z-10 max-w-xl animate-fade-in" style={{ animationDelay: '80ms' }}>
          <div className="text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: 'var(--gold)' }}>
            Edizione Enterprise · 2026
          </div>
          <h2 className="font-serif text-[3.2rem] text-white font-extralight tracking-tight leading-[1.06]">
            La precisione di una holding finanziaria,<br />
            l'agilità di un singolo agente.
          </h2>
          <p className="mt-7 text-slate-400 text-sm leading-[1.75] max-w-md">
            Pipeline PP &amp; CQS, dashboard direzionale, calcolatori DPR e debito residuo, AI Coach integrato: tutto in un'unica console operativa pensata per chi gestisce portafogli reali.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <div className="gold-rule" style={{ width: 28 }} />
            <div className="text-xs text-slate-500 italic font-light tracking-wide">
              "Discrezione, dato, decisione."
            </div>
          </div>
        </div>

        {/* Footer stats */}
        <div className="relative z-10 flex items-center gap-8 animate-fade-in" style={{ animationDelay: '160ms' }}>
          {[
            { v: '46', l: 'Moduli operativi' },
            { v: 'AI', l: 'Coach integrato' },
            { v: 'PWA', l: 'Ottimizzato mobile' },
          ].map(({ v, l }) => (
            <div key={v}>
              <div className="font-serif text-2xl font-light text-white">{v}</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-[420px] animate-fade-in" style={{ animationDelay: '40ms' }}>

          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <BALogo size={36} />
            <div>
              <div className="font-serif text-lg font-light">Best Agent</div>
              <div className="text-[9px] uppercase tracking-[0.24em]" style={{ color: 'var(--gold)' }}>Private Wealth CRM</div>
            </div>
          </div>

          {/* Form header */}
          <div className="text-[10px] uppercase tracking-[0.24em] mb-2" style={{ color: 'var(--gold)' }}>
            Accedi al CRM
          </div>
          <h1 className="font-serif text-[2.2rem] font-light tracking-tight text-slate-900 leading-none">
            Bentornato.
          </h1>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed">
            Inserisci le tue credenziali per accedere alla console operativa.
          </p>
          <div className="gold-rule mt-5 mb-8" />

          <form onSubmit={onSubmit} data-testid="login-form">
            {/* Email */}
            <div className="mb-5">
              <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
                Indirizzo email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="w-full px-4 py-3 text-sm border border-slate-200 bg-slate-50 hover:bg-white focus:bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/8 transition-all"
                style={{ borderRadius: 2, outline: 'none' }}
                placeholder="nome@dominio.it"
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Password
                </label>
                <Link to="/recupero" className="text-[11px] gold-link" data-testid="link-forgot">
                  Password dimenticata?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="w-full px-4 py-3 text-sm border border-slate-200 bg-slate-50 hover:bg-white focus:bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/8 transition-all"
                style={{ borderRadius: 2, outline: 'none' }}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            {/* Error */}
            {err && (
              <div
                className="mb-5 text-sm text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3"
                style={{ borderRadius: 2 }}
                data-testid="login-error"
              >
                <div className="font-medium">{err}</div>
                <div className="mt-2 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      setErr("Diagnostica in corso...");
                      const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/?_=" + Date.now();
                      try {
                        const ctrl = new AbortController();
                        const t = setTimeout(() => ctrl.abort(), 10000);
                        const r = await fetch(url, { method: "GET", credentials: "include", signal: ctrl.signal });
                        clearTimeout(t);
                        if (r.ok) setErr(`Backend raggiungibile (HTTP ${r.status}) — riprova il login`);
                        else setErr(`Backend risponde HTTP ${r.status}. Riprova tra poco.`);
                      } catch (ex) {
                        setErr(`Rete non raggiungibile: ${ex?.name} ${ex?.message || ""}. Verifica connessione.`);
                      }
                    }}
                    data-testid="login-diagnostic"
                    className="text-[11px] underline text-rose-700"
                  >
                    Diagnostica connessione
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        if ("serviceWorker" in navigator) {
                          const regs = await navigator.serviceWorker.getRegistrations();
                          await Promise.all(regs.map((r) => r.unregister()));
                        }
                        if ("caches" in window) {
                          const keys = await caches.keys();
                          await Promise.all(keys.map((k) => caches.delete(k)));
                        }
                        try { localStorage.clear(); sessionStorage.clear(); } catch {}
                      } catch {}
                      window.location.reload();
                    }}
                    data-testid="login-reset"
                    className="text-[11px] underline text-rose-700"
                  >
                    Svuota cache e ricarica
                  </button>
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full text-white font-medium py-3.5 flex items-center justify-center gap-2 transition-all disabled:opacity-60 text-sm tracking-wide"
              style={{ background: 'var(--navy)', borderRadius: 2 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--navy-3)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--navy)'}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Accedi alla console
            </button>
          </form>

          {/* Register link */}
          <div className="mt-6 text-xs text-slate-500 text-center">
            Non hai un account?{" "}
            <Link to="/register" className="gold-link font-medium" data-testid="link-register">
              Crea account
            </Link>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-[#F0F2F5] flex items-center justify-between">
            <div className="text-[10px] text-slate-400 tracking-wide">Best Agent · 2026</div>
            <div className="text-[10px] text-slate-400">Sessione cifrata · TLS 1.3</div>
          </div>
        </div>
      </div>
    </div>
  );
}
